import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { AppThunk, AppState } from ".";
import { apiClient } from "../utils/apiClient";
import type { Job } from "@services/pipeline/types";

// Note: API response typing issue - GetAllJobsResponse claims JobMetadata[] but API returns Job[]

type RequestStatus = "ready" | "pending" | "failure" | "success";

const setNestedProperty = (obj: any, path: string[], value: any) => {
  let current = obj;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }
  current[path[path.length - 1]] = value;
};

interface JobsStore {
  jobs: {
    requestStatus: RequestStatus;
    value?: Job[];
  };
  job: {
    [id: string]: {
      value?: Job;
      requestStatus: RequestStatus;
    };
  };
}

export const initialState: JobsStore = {
  jobs: {
    requestStatus: "ready",
  },
  job: {},
};

const slice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    startFetch: (state, { payload }: PayloadAction<{ key: string }>) => {
      const { key } = payload;
      const keyPath = key.split(".");
      setNestedProperty(state, [...keyPath, "requestStatus"], "pending");
    },
    fetchFailure: (
      state,
      { payload }: PayloadAction<{ key: string; error: any }>
    ) => {
      const { key, error } = payload;
      const keyPath = key.split(".");
      setNestedProperty(state, [...keyPath, "requestStatus"], "error");
      console.error(`fetch failed: ${key} - ${error}`);
    },
    fetchSuccess: (
      state,
      { payload }: PayloadAction<{ key: string; value: Job[] }>
    ) => {
      const { key, value } = payload;
      const keyPath = key.split(".");
      setNestedProperty(state, [...keyPath, "requestStatus"], "success");
      setNestedProperty(state, [...keyPath, "value"], value);
    },
    fetchJobsSuccess: (
      state,
      { payload }: PayloadAction<{ value: Job[] }>
    ) => {
      const { value } = payload;
      state.jobs.requestStatus = "success";
      state.jobs.value = value;
    },
  },
});

export const { startFetch, fetchFailure, fetchSuccess, fetchJobsSuccess } =
  slice.actions;

export const fetchAllJobs = (): AppThunk => async (dispatch, getState) => {
  dispatch(startFetch({ key: "jobs" }));
  try {
    const response =
      await apiClient.post<{ jobs: Job[] }>("jobs/");
    dispatch(fetchJobsSuccess({ value: response.jobs }));
  } catch (error) {
    dispatch(fetchFailure({ key: "jobs", error: "failed to fetch jobs" }));
  }
};

export const fetchKeyedValue =
  (id: string, path: string, api: string): AppThunk =>
  async (dispatch, getState) => {
    const key = `${path}.${id}`;
    dispatch(startFetch({ key }));
    try {
      const data = await apiClient.post<any>(`${api}${id}`);
      dispatch(fetchSuccess({ key, value: data }));
    } catch (error: any) {
      dispatch(fetchFailure({ key, error: error.message }));
    }
  };

export const fetchJob = (id: string): AppThunk => {
  return fetchKeyedValue(id, "job", "/jobs/j/");
};

export default slice.reducer;
